// Clase de utilidad para validar y corregir consultas SQL - ADAPTADA PARA SECTOR SALUD
export default class SQLValidator {
    constructor() {
        // Conflictos de tipos actualizados para el esquema de salud
        this.typeConflicts = [
            { pattern: /JOIN\s+\w+\s+\w+\s+ON\s+(\w+\.\w+)\s*=\s*(\w+\.\w+)/gi, types: ['bigint', 'text'] },
            { fields: ['"estudios"', '"importe"'], potentialConflict: true },
            // Conflictos potenciales entre BIGINT, DOUBLE PRECISION y TEXT
            { pattern: /WHERE\s+(\w+\.\w+|\w+)\s*[<>=!]+\s*'([^']+)'/gi, types: ['double precision', 'bigint', 'text'] }
        ];

        // Tablas del esquema de salud
        this.tables = {
            'public.ventas_medico': [
                '"fecha"', '"tipo_pac2"', '"clase_episodio"', '"centro_sanitario"', 
                '"id_medico"', '"nombre_medico"', '"descripcion_especialidad"', '"estudios"', '"importe"'
            ],
            'ventas_medico': [
                '"fecha"', '"tipo_pac2"', '"clase_episodio"', '"centro_sanitario"', 
                '"id_medico"', '"nombre_medico"', '"descripcion_especialidad"', '"estudios"', '"importe"'
            ],
            'public.ventas_sucursal': [
                '"fecha"', '"id_prestacion"', '"centro_sanitario"', '"descripcion_posicion"', '"estudios"', '"importe"'
            ],
            'ventas_sucursal': [
                '"fecha"', '"id_prestacion"', '"centro_sanitario"', '"descripcion_posicion"', '"estudios"', '"importe"'
            ]
        };

        // Alias de tabla del esquema de salud
        this.aliasMap = {
            'vm': 'ventas_medico',
            'vs': 'ventas_sucursal',
            'medico': 'ventas_medico',
            'sucursal': 'ventas_sucursal',
            'centro': 'ventas_medico',
            'prestacion': 'ventas_sucursal',
            'pvm': 'public.ventas_medico',
            'pvs': 'public.ventas_sucursal'
        };

        // Tipos de compatibilidad para el esquema de salud
        this.typeCompatibility = {
            // ventas_medico
            'ventas_medico."fecha"': 'text',
            'ventas_medico."tipo_pac2"': 'text',
            'ventas_medico."clase_episodio"': 'bigint',
            'ventas_medico."centro_sanitario"': 'text',
            'ventas_medico."id_medico"': 'text',
            'ventas_medico."nombre_medico"': 'text',
            'ventas_medico."descripcion_especialidad"': 'text',
            'ventas_medico."estudios"': 'bigint',
            'ventas_medico."importe"': 'double precision',

            // ventas_sucursal
            'ventas_sucursal."fecha"': 'text',
            'ventas_sucursal."id_prestacion"': 'text',
            'ventas_sucursal."centro_sanitario"': 'text',
            'ventas_sucursal."descripcion_posicion"': 'text',
            'ventas_sucursal."estudios"': 'text',
            'ventas_sucursal."importe"': 'text',

            // Con prefijo public. (copiar los mismos tipos)
            'public.ventas_medico."fecha"': 'text',
            'public.ventas_medico."tipo_pac2"': 'text',
            'public.ventas_medico."clase_episodio"': 'bigint',
            'public.ventas_medico."centro_sanitario"': 'text',
            'public.ventas_medico."id_medico"': 'text',
            'public.ventas_medico."nombre_medico"': 'text',
            'public.ventas_medico."descripcion_especialidad"': 'text',
            'public.ventas_medico."estudios"': 'bigint',
            'public.ventas_medico."importe"': 'double precision',

            'public.ventas_sucursal."fecha"': 'text',
            'public.ventas_sucursal."id_prestacion"': 'text',
            'public.ventas_sucursal."centro_sanitario"': 'text',
            'public.ventas_sucursal."descripcion_posicion"': 'text',
            'public.ventas_sucursal."estudios"': 'text',
            'public.ventas_sucursal."importe"': 'text'
        };
    }

    // MÉTODO cleanMarkdown MEJORADO (mantenido igual)
    cleanMarkdown(sql) {
        if (!sql || typeof sql !== 'string') return sql;

        console.log("🔧 DEBUG cleanMarkdown - Input:", sql);

        let cleanedSQL = sql;

        // 1. Limpiar formato Markdown básico
        cleanedSQL = cleanedSQL.replace(/^```sql\s*/gm, '');
        cleanedSQL = cleanedSQL.replace(/^```\s*/gm, '');
        cleanedSQL = cleanedSQL.replace(/\s*```$/gm, '');

        // 2. Limpiar comentarios con # (estilo Python/Shell)
        cleanedSQL = cleanedSQL.replace(/^#.*$/gm, '');
        cleanedSQL = cleanedSQL.replace(/\s*#[^\r\n]*/g, '');

        // 3. Limpiar títulos y texto descriptivo común
        cleanedSQL = cleanedSQL.replace(/^Consulta SQL.*$/gmi, '');
        cleanedSQL = cleanedSQL.replace(/^Query.*$/gmi, '');
        cleanedSQL = cleanedSQL.replace(/^Here.*SQL.*$/gmi, '');
        cleanedSQL = cleanedSQL.replace(/^Aquí.*SQL.*$/gmi, '');
        cleanedSQL = cleanedSQL.replace(/^La consulta.*$/gmi, '');
        cleanedSQL = cleanedSQL.replace(/^Esta consulta.*$/gmi, '');

        // 4. ✅ NUEVO: Extracción de SQL más inteligente
        const sqlStartPattern = /(?:^|\n)\s*((?:SELECT|WITH|INSERT|UPDATE|DELETE)\b)/im;
        const sqlStartMatch = cleanedSQL.match(sqlStartPattern);

        if (sqlStartMatch) {
            const startIndex = cleanedSQL.indexOf(sqlStartMatch[1]);
            let sqlPart = cleanedSQL.substring(startIndex);

            console.log("🔧 DEBUG cleanMarkdown - SQL encontrado desde posición:", startIndex);
            console.log("🔧 DEBUG cleanMarkdown - SQL parte extraída:", sqlPart.substring(0, 100) + "...");

            // Buscar donde termina el SQL válido
            const endPatterns = [
                /\n\s*(?:Esta consulta|This query|Explanation|Explicación|Note|Nota)/i,
                /\n\s*(?:El resultado|The result|Como se puede|As you can)/i,
                /\n\s*(?:\*\*|##|\d+\.)/m  // Markdown formatting o listas numeradas
            ];

            let endIndex = sqlPart.length;
            for (const pattern of endPatterns) {
                const match = sqlPart.match(pattern);
                if (match && match.index < endIndex) {
                    endIndex = match.index;
                }
            }

            cleanedSQL = sqlPart.substring(0, endIndex);
            console.log("🔧 DEBUG cleanMarkdown - SQL después de corte de final:", cleanedSQL.substring(0, 100) + "...");
        }

        // 5. Limpiar líneas vacías múltiples al principio y final
        cleanedSQL = cleanedSQL.trim();
        cleanedSQL = cleanedSQL.replace(/^\s*\n+/g, '');
        cleanedSQL = cleanedSQL.replace(/\n\s*\n\s*\n/g, '\n\n');

        // 6. Asegurar que termine con punto y coma
        if (cleanedSQL && !cleanedSQL.trim().endsWith(';')) {
            cleanedSQL = cleanedSQL.trim() + ';';
        }

        // 7. ✅ VALIDACIÓN FINAL: Verificar que el SQL tiene estructura básica
        const hasSelect = /\bSELECT\b/i.test(cleanedSQL);
        const hasFrom = /\bFROM\b/i.test(cleanedSQL);
        const hasWithOrSelect = /\b(WITH|SELECT)\b/i.test(cleanedSQL);

        if (!hasWithOrSelect) {
            console.error("🚨 DEBUG cleanMarkdown - SQL no válido: no tiene SELECT o WITH");
            console.error("🚨 DEBUG cleanMarkdown - Devolviendo SQL original");
            return sql;
        }

        if (cleanedSQL.toUpperCase().startsWith('SELECT') && !hasFrom) {
            console.error("🚨 DEBUG cleanMarkdown - SELECT sin FROM detectado");
            console.error("🚨 DEBUG cleanMarkdown - Devolviendo SQL original");
            return sql;
        }

        console.log("🔧 DEBUG cleanMarkdown - Output final:", cleanedSQL);

        return cleanedSQL;
    }

    // Verificar estructuras incompletas en SQL
    checkIncompleteStructures(sql) {
        const errors = [];

        // Verificar CASE/END balanceados
        const caseCount = (sql.match(/\bCASE\b/gi) || []).length;
        const endCount = (sql.match(/\bEND\b/gi) || []).length;

        if (caseCount > endCount) {
            errors.push(`Hay ${caseCount - endCount} declaraciones CASE sin cerrar con END`);
        }

        // Verificación para operaciones date-interval (adaptado para fechas TEXT formato AAAA-MM)
        const dateIntervalOps = [...sql.matchAll(/(\w+\.\w+|\w+)\s*([<>=!]+)\s*.*?\s*(-|\+)\s*INTERVAL\s+\'([^\']+)\'/gi)];

        for (const match of dateIntervalOps) {
            const column = match[1];
            if (column.toLowerCase().includes('fecha') && !sql.includes(`CAST(${column}`)) {
                errors.push(`Posible error de tipo: columna '${column}' usada con INTERVAL sin CAST a DATE`);
            }
        }

        // Verificar subconsultas con MAX(fecha) o similar
        if (sql.includes("MAX(fecha)") &&
            sql.includes("INTERVAL") &&
            !sql.includes("CAST") &&
            sql.includes("-")) {
            errors.push("Posible error de incompatibilidad entre fecha como TEXT e INTERVAL");
        }

        // Verificar operaciones aritméticas con INTERVAL sin CAST
        const intervalPatterns = [
            /\bINTERVAL\b.*?(-|\+)\s*(\w+\.\w+|\w+)/gi,
            /(\w+\.\w+|\w+)\s*(-|\+)\s*\bINTERVAL\b/gi
        ];

        for (const pattern of intervalPatterns) {
            const matches = [...sql.matchAll(pattern)];
            for (const match of matches) {
                const operand = match[2] || match[1];
                if (operand.toLowerCase().includes('fecha') && !sql.includes(`CAST(${operand}`)) {
                    errors.push(`Operación aritmética entre INTERVAL y '${operand}' sin CAST adecuado`);
                }
            }
        }

        // Verificar declaraciones CASE incompletas
        const incompleteCase = sql.match(/CASE\s*;/gi);
        if (incompleteCase) {
            errors.push('Declaración CASE incompleta encontrada');
        }

        // Verificar cláusulas WITH sin SELECT
        if (sql.includes('WITH') && !sql.includes('SELECT')) {
            errors.push('Cláusula WITH sin SELECT correspondiente');
        }

        return errors;
    }

    // Validar sintaxis básica de SQL
    validate(sql) {
        if (!sql || typeof sql !== 'string') {
            return { valid: false, errors: ['Consulta SQL vacía o inválida'] };
        }

        sql = sql.trim();
        const errors = [];

        // Verificaciones básicas
        if (!sql.toLowerCase().includes('select')) {
            errors.push('La consulta debe incluir una cláusula SELECT');
        }

        if (!sql.toLowerCase().includes('from')) {
            errors.push('La consulta debe incluir una cláusula FROM');
        }

        // Verificar paréntesis balanceados
        const openParens = (sql.match(/\(/g) || []).length;
        const closeParens = (sql.match(/\)/g) || []).length;
        if (openParens !== closeParens) {
            errors.push(`Paréntesis desbalanceados: ${openParens} abiertos vs ${closeParens} cerrados`);
        }

        // Verificar comillas balanceadas
        const quotes = (sql.match(/'/g) || []).length;
        if (quotes % 2 !== 0) {
            errors.push('Comillas simples desbalanceadas');
        }

        // Verificar alias de tablas
        for (const [alias, table] of Object.entries(this.aliasMap)) {
            if (sql.includes(`${alias}.`) &&
                !sql.includes(`${table} ${alias}`) &&
                !sql.includes(`${table} AS ${alias}`) &&
                !sql.includes(`public.${table} ${alias}`) &&
                !sql.includes(`public.${table} AS ${alias}`)) {
                errors.push(`Referencia a alias "${alias}" sin definición previa`);
            }
        }

        return {
            valid: errors.length === 0,
            errors: errors
        };
    }

    // Método principal para corregir consultas SQL (ADAPTADO PARA SECTOR SALUD)
    fixQuery(sql, errorMessage = '') {
        let fixedSQL = sql.trim();
        let wasFixed = false;
        let fixes = [];

        // Limpiar formato Markdown
        const originalSQL = fixedSQL;
        fixedSQL = this.cleanMarkdown(fixedSQL);
        if (originalSQL !== fixedSQL) {
            wasFixed = true;
            fixes.push('Eliminado formato Markdown');
        }

        // Mapeo de columnas comunes y sus correcciones para el esquema de salud
        const columnReplacements = {
            // Mapeo para el esquema de salud
            'fecha': '"fecha"',
            'date': '"fecha"',
            'tipo_paciente': '"tipo_pac2"',
            'tipo_pac': '"tipo_pac2"',
            'paciente': '"tipo_pac2"',
            'episodio': '"clase_episodio"',
            'clase': '"clase_episodio"',
            'centro': '"centro_sanitario"',
            'hospital': '"centro_sanitario"',
            'sucursal': '"centro_sanitario"',
            'id_doctor': '"id_medico"',
            'doctor_id': '"id_medico"',
            'medico_id': '"id_medico"',
            'nombre_doctor': '"nombre_medico"',
            'doctor': '"nombre_medico"',
            'medico': '"nombre_medico"',
            'especialidad': '"descripcion_especialidad"',
            'spec': '"descripcion_especialidad"',
            'specialty': '"descripcion_especialidad"',
            'estudios': '"estudios"',
            'procedimientos': '"estudios"',
            'procedures': '"estudios"',
            'cantidad': '"estudios"',
            'importe': '"importe"',
            'facturacion': '"importe"',
            'monto': '"importe"',
            'amount': '"importe"',
            'precio': '"importe"',
            'price': '"importe"',
            'prestacion': '"id_prestacion"',
            'prestacion_id': '"id_prestacion"',
            'descripcion': '"descripcion_posicion"',
            'description': '"descripcion_posicion"',
            'procedimiento': '"descripcion_posicion"',

            // Campos comunes que no existen pero podrían mapearse
            'año': null,                      // Se maneja con fecha
            'year': null,
            'mes': null,                      // Se maneja con fecha
            'month': null,
            'cliente': null,                  // No existe en este esquema
            'customer': null,
            'producto': null,                 // No existe en este esquema
            'product': null
        };

        for (const [oldColumn, newColumn] of Object.entries(columnReplacements)) {
            if (fixedSQL.includes(oldColumn)) {
                if (newColumn) {
                    const beforeColumnFix = fixedSQL;
                    fixedSQL = fixedSQL.replace(new RegExp(`\\b${oldColumn}\\b`, 'g'), newColumn);
                    if (beforeColumnFix !== fixedSQL) {
                        wasFixed = true;
                        fixes.push(`Actualizado nombre de columna: ${oldColumn} → ${newColumn}`);
                    }
                } else {
                    fixes.push(`ADVERTENCIA: Columna ${oldColumn} no existe en el esquema actual`);
                }
            }
        }

        // Corregir conversiones de tipo para ventas_sucursal (TEXT a NUMERIC/INTEGER)
        if (fixedSQL.includes('ventas_sucursal') && 
            (fixedSQL.includes('"estudios"') || fixedSQL.includes('"importe"'))) {
            
            const beforeTypeFix = fixedSQL;
            
            // Corregir estudios en ventas_sucursal (TEXT a INTEGER)
            fixedSQL = fixedSQL.replace(
                /(ventas_sucursal\."estudios"|vs\."estudios")/g,
                'CAST($1 AS INTEGER)'
            );
            
            // Corregir importe en ventas_sucursal (TEXT a NUMERIC)
            fixedSQL = fixedSQL.replace(
                /(ventas_sucursal\."importe"|vs\."importe")/g,
                'CAST($1 AS NUMERIC)'
            );
            
            if (beforeTypeFix !== fixedSQL) {
                wasFixed = true;
                fixes.push('Añadido CAST para campos TEXT en ventas_sucursal');
            }
        }

        // Corregir función ROUND con mejor manejo
        if (errorMessage.includes("function round") && errorMessage.includes("does not exist")) {
            const beforeRoundFix = fixedSQL;

            console.log("🔧 DEBUG - Aplicando fix para función ROUND");

            // 1. Fix básico: ROUND con operaciones simples
            fixedSQL = fixedSQL.replace(
                /ROUND\s*\(\s*([^,()]+)\s*,\s*([0-9]+)\s*\)/gi,
                'ROUND(CAST($1 AS NUMERIC), $2)'
            );

            // 2. Fix para ROUND con expresiones aritméticas simples
            fixedSQL = fixedSQL.replace(
                /ROUND\s*\(\s*\(\s*([^()]+)\s*\)\s*,\s*([0-9]+)\s*\)/gi,
                'ROUND(CAST(($1) AS NUMERIC), $2)'
            );

            // 3. Fix para ROUND con divisiones específicas
            fixedSQL = fixedSQL.replace(
                /ROUND\s*\(\s*(\w+\.\w+|\w+)\s*\/\s*NULLIF\s*\(\s*([^)]+)\s*\)\s*,\s*([0-9]+)\s*\)/gi,
                'ROUND(CAST($1 AS NUMERIC) / NULLIF(CAST($2 AS NUMERIC), 0), $3)'
            );

            // 4. Fix para ROUND con funciones agregadas
            fixedSQL = fixedSQL.replace(
                /ROUND\s*\(\s*(AVG|SUM|MIN|MAX|COUNT)\s*\(\s*([^)]+)\s*\)\s*,\s*([0-9]+)\s*\)/gi,
                'ROUND($1($2)::NUMERIC, $3)'
            );

            if (beforeRoundFix !== fixedSQL) {
                wasFixed = true;
                fixes.push('Añadido cast a NUMERIC para función ROUND');
            }
        }

        // Corregir problemas con fechas (formato AAAA-MM como TEXT)
        if (errorMessage.includes("operator does not exist") &&
            (errorMessage.includes("fecha") || errorMessage.includes("date"))) {

            const beforeDateFix = fixedSQL;

            // Corregir operaciones de fecha que requieren conversión
            const dateFunctionPattern = /(EXTRACT|DATE_PART|DATE_TRUNC)\s*\(\s*\'?(\w+)\'?\s*,\s*(\w+\.\w+|\w+)\s*\)/gi;

            fixedSQL = fixedSQL.replace(dateFunctionPattern, (match, func, part, column) => {
                if (column.toLowerCase().includes('fecha')) {
                    return `${func}('${part}', CAST(${column} AS DATE))`;
                }
                return match;
            });

            // Corregir comparaciones de fecha con INTERVAL
            const dateIntervalPattern = /(\w+\.\w+|\w+)\s*([<>=!]+)\s*(.+?)\s*(-|\+)\s*INTERVAL\s+\'([^\']+)\'/gi;

            fixedSQL = fixedSQL.replace(dateIntervalPattern, (match, column, operator, value, arithmeticOp, interval) => {
                let fixedColumn = column;
                if (column.toLowerCase().includes('fecha')) {
                    fixedColumn = `CAST(${column} AS DATE)`;
                }
                return `${fixedColumn} ${operator} ${value} ${arithmeticOp} INTERVAL '${interval}'`;
            });

            if (beforeDateFix !== fixedSQL) {
                wasFixed = true;
                fixes.push('Aplicado CAST para operaciones de fecha');
            }
        }

        // Corregir errores de sintaxis con AS
        if (errorMessage.includes("syntax error at or near") && errorMessage.includes("AS")) {
            const beforeSyntaxFix = fixedSQL;

            // Corregir CAST mal formateados
            fixedSQL = fixedSQL.replace(/CAST\s*\(\s*([^)]+)\s+AS\s+NUMERIC\s*\)/gi, 'CAST($1 AS NUMERIC)');
            fixedSQL = fixedSQL.replace(/CAST\s*\(\s*CAST\s*\(\s*([^)]+)\s*AS\s*NUMERIC\s*\)\s*AS\s*NUMERIC\s*\)/gi, 'CAST($1 AS NUMERIC)');

            if (beforeSyntaxFix !== fixedSQL) {
                wasFixed = true;
                fixes.push('Corregida sintaxis de CAST/AS');
            }
        }

        // Corregir declaraciones CASE incompletas
        if (errorMessage.includes("syntax error") &&
            (errorMessage.includes("CASE") || fixedSQL.includes("CASE;"))) {

            const beforeCaseFix = fixedSQL;

            // Remover CASE; incompletos
            fixedSQL = fixedSQL.replace(/CASE\s*;/g, '');

            // Corregir CASE sin END
            const caseMatches = [...fixedSQL.matchAll(/(\bCASE\b(?:(?!\bEND\b).)*?)(?=\s*(?:FROM|WHERE|GROUP|ORDER|LIMIT|;|$))/gis)];

            for (const match of caseMatches) {
                const incompleteCase = match[0];
                if (!incompleteCase.includes('END')) {
                    if (incompleteCase.includes('WHEN')) {
                        fixedSQL = fixedSQL.replace(incompleteCase, `${incompleteCase} END`);
                    } else {
                        fixedSQL = fixedSQL.replace(incompleteCase, '');
                    }
                }
            }

            if (beforeCaseFix !== fixedSQL) {
                wasFixed = true;
                fixes.push('Corregidas declaraciones CASE incompletas');
            }
        }

        // Operaciones aritméticas con problemas de tipo
        if (errorMessage.includes("operator does not exist") &&
            (errorMessage.includes("+") || errorMessage.includes("-") ||
                errorMessage.includes("*") || errorMessage.includes("/"))) {

            const beforeOpFix = fixedSQL;

            fixedSQL = fixedSQL.replace(
                /(\w+\.\w+|\w+)\s*(\+|\-|\*|\/)\s*(\w+\.\w+|\w+)/g,
                (match, left, op, right) => {
                    if (left.includes('CAST') || right.includes('CAST') ||
                        !isNaN(parseFloat(left)) || !isNaN(parseFloat(right))) {
                        return match;
                    }
                    return `CAST(${left} AS numeric) ${op} CAST(${right} AS numeric)`;
                }
            );

            if (beforeOpFix !== fixedSQL) {
                wasFixed = true;
                fixes.push('Añadido cast explícito para operaciones aritméticas');
            }
        }

        // División especial (prevenir división por cero)
        if (errorMessage.includes("division by zero") ||
            (errorMessage.includes("/") && errorMessage.includes("operator does not exist"))) {

            const beforeDivFix = fixedSQL;

            fixedSQL = fixedSQL.replace(
                /(\w+\.\w+|\w+)\s*\/\s*(\w+\.\w+|\w+)/g,
                (match, numerator, denominator) => {
                    if (match.includes('NULLIF')) return match;
                    return `CAST(${numerator} AS numeric) / NULLIF(CAST(${denominator} AS numeric), 0)`;
                }
            );

            if (beforeDivFix !== fixedSQL) {
                wasFixed = true;
                fixes.push('Añadida protección contra división por cero');
            }
        }

        // Corregir errores de incompatibilidad de tipos específicos del sector salud
        if (errorMessage.includes("operator does not exist") &&
            (errorMessage.includes("bigint = text") ||
                errorMessage.includes("text = bigint") ||
                errorMessage.includes("double precision = text"))) {

            const beforeTypeFix = fixedSQL;

            // Para campos específicos del sector salud
            if (errorMessage.includes("bigint") && errorMessage.includes("text")) {
                fixedSQL = fixedSQL.replace(
                    /("estudios"|"clase_episodio")\s*=\s*("[^"]+"|'[^']+')/gi,
                    '$1 = CAST($2 AS BIGINT)'
                );
            }

            if (errorMessage.includes("double precision") && errorMessage.includes("text")) {
                fixedSQL = fixedSQL.replace(
                    /("importe")\s*=\s*("[^"]+"|'[^']+')/gi,
                    '$1 = CAST($2 AS DOUBLE PRECISION)'
                );
            }

            if (beforeTypeFix !== fixedSQL) {
                wasFixed = true;
                fixes.push('Corregida incompatibilidad de tipos en campos del sector salud');
            }
        }

        // Corregir errores de alias sin declarar
        for (const [alias, table] of Object.entries(this.aliasMap)) {
            if (fixedSQL.includes(`${alias}.`) &&
                !fixedSQL.includes(`${table} ${alias}`) &&
                !fixedSQL.includes(`${table} AS ${alias}`) &&
                !fixedSQL.includes(`public.${table} ${alias}`) &&
                !fixedSQL.includes(`public.${table} AS ${alias}`)) {

                if (fixedSQL.includes(`FROM ${table}`) || fixedSQL.includes(`FROM public.${table}`)) {
                    const beforeAliasFix = fixedSQL;
                    fixedSQL = fixedSQL.replace(`FROM ${table}`, `FROM ${table} ${alias}`)
                        .replace(`FROM public.${table}`, `FROM public.${table} ${alias}`);

                    if (beforeAliasFix !== fixedSQL) {
                        wasFixed = true;
                        fixes.push(`Añadido alias "${alias}" a la tabla "${table}"`);
                    }
                } else {
                    const beforeAliasFix = fixedSQL;
                    fixedSQL = fixedSQL.replace(new RegExp(`${alias}\\.`, 'g'), `${table}.`);

                    if (beforeAliasFix !== fixedSQL) {
                        wasFixed = true;
                        fixes.push(`Reemplazado alias "${alias}" por nombre completo "${table}"`);
                    }
                }
            }
        }

        // Corregir WHERE con operador lógico incorrecto
        if (/WHERE\s+AND/i.test(fixedSQL) || /WHERE\s+OR/i.test(fixedSQL)) {
            const beforeWhereFix = fixedSQL;
            fixedSQL = fixedSQL.replace(/WHERE\s+AND/i, 'WHERE').replace(/WHERE\s+OR/i, 'WHERE');

            if (beforeWhereFix !== fixedSQL) {
                wasFixed = true;
                fixes.push('Eliminado operador lógico innecesario después de WHERE');
            }
        }

        // Añadir punto y coma al final
        if (!fixedSQL.trim().endsWith(';')) {
            fixedSQL = fixedSQL.trim() + ';';
            wasFixed = true;
            fixes.push('Añadido punto y coma al final');
        }

        // Correcciones específicas basadas en mensajes de error
        if (errorMessage) {
            // Error de tabla no existente
            if (errorMessage.includes('relation') && errorMessage.includes('does not exist')) {
                const match = errorMessage.match(/relation\s+"?([^"]+)"?/i);
                if (match && match[1]) {
                    const badTable = match[1].trim();
                    let bestMatch = null;
                    let bestSimilarity = 0;

                    for (const table of Object.keys(this.tables)) {
                        const similarity = this.stringSimilarity(badTable, table);
                        if (similarity > bestSimilarity) {
                            bestSimilarity = similarity;
                            bestMatch = table;
                        }
                    }

                    if (bestMatch && bestSimilarity > 0.5) {
                        const beforeTableFix = fixedSQL;
                        fixedSQL = fixedSQL.replace(new RegExp(`\\b${badTable}\\b`, 'g'), bestMatch);

                        if (beforeTableFix !== fixedSQL) {
                            wasFixed = true;
                            fixes.push(`Corregido nombre de tabla "${badTable}" a "${bestMatch}"`);
                        }
                    }
                }
            }

            // Error de columna no existente
            if (errorMessage.includes('column') && errorMessage.includes('does not exist')) {
                const match = errorMessage.match(/column\s+"?([^"]+)"?/i);
                if (match && match[1]) {
                    const badColumn = match[1].trim();

                    if (columnReplacements[badColumn]) {
                        const beforeColumnFix = fixedSQL;
                        fixedSQL = fixedSQL.replace(
                            new RegExp(`\\b${badColumn}\\b`, 'g'),
                            columnReplacements[badColumn]
                        );

                        if (beforeColumnFix !== fixedSQL) {
                            wasFixed = true;
                            fixes.push(`Corregido nombre de columna "${badColumn}" a "${columnReplacements[badColumn]}"`);
                        }
                    }
                }
            }

            // Timeouts y consultas lentas
            if (errorMessage.includes("timeout") ||
                errorMessage.includes("execution time exceeded") ||
                errorMessage.includes("canceling statement due to")) {

                const beforeTimeoutFix = fixedSQL;

                if (!fixedSQL.toLowerCase().includes("limit")) {
                    fixedSQL = fixedSQL.replace(/;$/, " LIMIT 1000;");
                }

                if (beforeTimeoutFix !== fixedSQL) {
                    wasFixed = true;
                    fixes.push("Optimizada consulta para evitar timeout añadiendo LIMIT");
                }
            }
        }

        return {
            sql: fixedSQL,
            wasFixed,
            fixes
        };
    }

    // Calcular similitud entre cadenas (mantenido igual)
    stringSimilarity(s1, s2) {
        if (!s1 || !s2) return 0;
        s1 = s1.toLowerCase();
        s2 = s2.toLowerCase();
        if (s1 === s2) return 1.0;

        let matches = 0;
        for (let i = 0; i < Math.min(s1.length, s2.length); i++) {
            if (s1[i] === s2[i]) matches++;
        }
        return matches / Math.max(s1.length, s2.length);
    }

    // Procesar consulta completa (mantenido igual)
    processQuery(sql, errorMessage = '') {
        sql = this.cleanMarkdown(sql);

        const structuralErrors = this.checkIncompleteStructures(sql);
        if (structuralErrors.length > 0) {
            console.warn("Errores estructurales detectados:", structuralErrors);
        }

        const unbalancedElements = this.checkUnbalancedElements(sql);
        if (unbalancedElements.length > 0) {
            console.warn("Elementos no balanceados:", unbalancedElements);
        }

        const validationResult = this.validate(sql);

        if (!validationResult.valid || errorMessage ||
            structuralErrors.length > 0 || unbalancedElements.length > 0) {
            return this.fixQuery(sql, errorMessage);
        }

        return {
            sql: sql,
            wasFixed: false,
            fixes: []
        };
    }

    // Verificar elementos desbalanceados (mantenido igual)
    checkUnbalancedElements(sql) {
        const errors = [];

        const openParens = (sql.match(/\(/g) || []).length;
        const closeParens = (sql.match(/\)/g) || []).length;
        if (openParens !== closeParens) {
            errors.push(`Paréntesis desbalanceados: ${openParens} abiertos vs ${closeParens} cerrados`);
        }

        const singleQuotes = (sql.match(/'/g) || []).length;
        if (singleQuotes % 2 !== 0) {
            errors.push('Comillas simples desbalanceadas');
        }

        const doubleQuotes = (sql.match(/"/g) || []).length;
        if (doubleQuotes % 2 !== 0) {
            errors.push('Comillas dobles desbalanceadas');
        }

        return errors;
    }
}